/**
 * RoiPanel — Commercial-HP-style 4-tech TCO comparison panel.
 *
 * Differs from the legacy `frontend/src/pages/sales/MeetingMode/_shared/RoiPanel.tsx`
 * (which renders a wide table). This is the reference-HTML-matched layout used
 * by the new exact-match calc pages: 4 cards (HP highlighted) + 3 savings cards
 * + cumulative-cost SVG chart + notes.
 *
 * Plan ref: docs/superpowers/plans/2026-05-15-calculator-exact-match-replacement.md §3.2 Step 6.
 */
import { RoiCumulativeChart, type RoiSeries } from './RoiCumulativeChart';

export interface TechCard {
  name: string;
  effLabel: string;
  capex: number;
  dailyEnergyUnits: string;
  annualFuel: number;
  annualMaint: number;
  annualOpex: number;
  fiveYearTCO: number;
  tenYearTCO: number;
  annualSavingsVsHP?: number;
  capexPremiumVsHP?: number;
  paybackYears?: number | null;
}

export interface RoiData {
  hp: TechCard;
  elec: TechCard;
  diesel: TechCard;
  gas: TechCard;
  cumulative: RoiSeries;
  notes: string[];
}

const fmt = (n: number) => '₹' + Math.round(n).toLocaleString('en-IN');

// Tailwind classes for per-rival savings card accents — match ROI_TECHS bg/color.
const RIVAL_ACCENT = {
  elec: { border: 'border-amber-300', text: 'text-amber-700', bg: 'bg-amber-50' },
  diesel: { border: 'border-rose-300', text: 'text-rose-800', bg: 'bg-rose-50' },
  gas: { border: 'border-blue-300', text: 'text-blue-800', bg: 'bg-blue-50' },
} as const;

export function RoiPanel({ data }: { data: RoiData }) {
  return (
    <div className="space-y-4">
      {/* 4-tech cards (HP highlighted) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {(['hp', 'elec', 'diesel', 'gas'] as const).map((k) => {
          const t = data[k];
          const isHp = k === 'hp';
          return (
            <div
              key={k}
              className={`rounded-lg border ${
                isHp ? 'border-emerald-600 border-2' : 'border-slate-200'
              } bg-white p-4`}
            >
              <div className="text-xs font-medium uppercase tracking-wide text-slate-600">
                {t.name}
                {isHp && (
                  <span className="ml-2 inline-block rounded bg-emerald-600 text-white px-2 py-0.5 text-[10px]">
                    Recommended
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500">{t.effLabel}</div>
              <table className="mt-2 w-full text-xs">
                <tbody>
                  <tr>
                    <td className="py-1 text-slate-500">CAPEX</td>
                    <td className="text-right">{fmt(t.capex)}</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-slate-500">Daily fuel</td>
                    <td className="text-right">{t.dailyEnergyUnits}</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-slate-500">Annual fuel</td>
                    <td className="text-right">{fmt(t.annualFuel)}</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-slate-500">Annual maint.</td>
                    <td className="text-right">{fmt(t.annualMaint)}</td>
                  </tr>
                  <tr className="border-t border-slate-100">
                    <td className="py-1 font-medium">Annual OPEX</td>
                    <td className="text-right font-medium">
                      {fmt(t.annualOpex)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1 text-slate-500">5-yr TCO</td>
                    <td className="text-right">{fmt(t.fiveYearTCO)}</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-slate-500">10-yr TCO</td>
                    <td className="text-right">{fmt(t.tenYearTCO)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      {/* 3 HP-vs-each savings cards (only when backend supplied the per-rival fields) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(['elec', 'diesel', 'gas'] as const).map((k) => {
          const t = data[k];
          const accent = RIVAL_ACCENT[k];
          const fiveYearSavings = t.fiveYearTCO - data.hp.fiveYearTCO;
          const tenYearSavings = t.tenYearTCO - data.hp.tenYearTCO;
          let paybackText: React.ReactNode;
          if (t.paybackYears === 0) {
            paybackText = (
              <span className={accent.text}>
                <strong>HP cheaper from day 1</strong>
              </span>
            );
          } else if (t.paybackYears != null) {
            paybackText = <strong>{t.paybackYears.toFixed(1)} years</strong>;
          } else if (t.annualSavingsVsHP != null) {
            paybackText = (
              <span className="text-amber-700">
                No payback (HP costs more annually)
              </span>
            );
          } else {
            paybackText = <span className="text-slate-500">—</span>;
          }
          return (
            <div
              key={k}
              className={`rounded-md border ${accent.border} ${accent.bg} p-3.5`}
            >
              <p className="m-0 mb-2 text-xs text-slate-600">
                Heat Pump vs{' '}
                <strong className={accent.text}>{t.name}</strong>
              </p>
              <table className="w-full text-xs">
                <tbody>
                  <tr>
                    <td className="py-1 text-slate-500">Annual savings</td>
                    <td className="text-right py-1 font-medium">
                      {t.annualSavingsVsHP != null
                        ? fmt(t.annualSavingsVsHP)
                        : '—'}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1 text-slate-500">CAPEX premium</td>
                    <td className="text-right py-1">
                      {t.capexPremiumVsHP != null
                        ? fmt(t.capexPremiumVsHP)
                        : '—'}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1 text-slate-500">Simple payback</td>
                    <td className="text-right py-1">{paybackText}</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-slate-500">5-year savings</td>
                    <td
                      className={`text-right py-1 font-medium ${accent.text}`}
                    >
                      {fmt(fiveYearSavings)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1 text-slate-500">10-year savings</td>
                    <td
                      className={`text-right py-1 font-medium ${accent.text}`}
                    >
                      {fmt(tenYearSavings)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      {/* Cumulative-cost chart */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h4 className="text-sm font-medium mb-2 text-slate-900">
          Cumulative cost over 5 years
        </h4>
        <RoiCumulativeChart data={data.cumulative} />
      </div>

      {/* Calculation notes */}
      {data.notes.length > 0 && (
        <ul className="text-xs text-slate-600 list-disc pl-5 space-y-1">
          {data.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
